import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
  Grid,
  Button,
  Avatar,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  Person as PersonIcon,
  School as SchoolIcon,
  MenuBook as ModuleIcon,
  ArrowForward as ArrowIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Room as RoomIcon,
  Schedule as ScheduleIcon,
  Info as InfoIcon,
  ContactMail as ContactIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import {
  getStudentTeachers,
  TeacherProfile,
  Avatar as UserAvatar,
} from '../../services/userService';
import { useAuth } from '../../hooks/useAuth';
import AvatarDisplay from '../shared/AvatarDisplay';

interface Teacher {
  user_id: string;
  name: string;
  email: string;
  school_id: string;
  avatar?: UserAvatar;
  teacher_profile?: TeacherProfile;
}

export const MyTeachers: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [teachers, setTeachers] = useState<Teacher[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedTeacher, setSelectedTeacher] = useState<Teacher | null>(null);
  const [profileDialogOpen, setProfileDialogOpen] = useState(false);

  useEffect(() => {
    if (user) {
      loadTeachers();
    }
  }, [user]);

  const loadTeachers = async () => {
    if (!user) return;

    try {
      setLoading(true);
      setError(null);

      // Use 'me' as the user ID - the backend will resolve this to the authenticated user's DynamoDB user_id
      const result = await getStudentTeachers('me');
      setTeachers(result.teachers || []);
    } catch (err: any) {
      console.error('Error loading teachers:', err);
      setError(err.message || 'Failed to load teachers');
    } finally {
      setLoading(false);
    }
  };

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .substring(0, 2);
  };

  const handleViewModules = (teacherId: string) => {
    navigate(`/student/teachers/${teacherId}/modules`);
  };

  const handleViewProfile = (teacher: Teacher) => {
    setSelectedTeacher(teacher);
    setProfileDialogOpen(true);
  };

  const handleCloseProfile = () => {
    setProfileDialogOpen(false);
    setSelectedTeacher(null);
  };

  if (loading) {
    return (
      <Box
        sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" gutterBottom sx={{ fontWeight: 600 }}>
          My Teachers
        </Typography>
        <Typography variant="body1" color="text.secondary">
          View your assigned teachers and their modules
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Teachers Grid */}
      {teachers.length === 0 ? (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 8 }}>
            <SchoolIcon sx={{ fontSize: 80, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No teachers assigned yet
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Your school administrator will assign teachers to you soon
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <Grid container spacing={3}>
          {teachers.map((teacher) => (
            <Grid item xs={12} sm={6} md={4} key={teacher.user_id}>
              <Card
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    boxShadow: 6,
                    transform: 'translateY(-4px)',
                  },
                }}
              >
                <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                  {/* Teacher Avatar and Name */}
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                    <Box sx={{ mr: 2 }}>
                      {teacher.avatar ? (
                        <AvatarDisplay
                          avatar={teacher.avatar}
                          name={teacher.name}
                          role="teacher"
                          size="large"
                          showTooltip={false}
                        />
                      ) : (
                        <Avatar
                          sx={{
                            width: 64,
                            height: 64,
                            bgcolor: 'primary.main',
                            fontSize: '1.5rem',
                            fontWeight: 600,
                          }}
                        >
                          {getInitials(teacher.name)}
                        </Avatar>
                      )}
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                        {teacher.name}
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ fontSize: '0.875rem' }}
                      >
                        {teacher.email}
                      </Typography>
                    </Box>
                  </Box>

                  {/* Teacher Profile Info */}
                  {teacher.teacher_profile && (
                    <Box sx={{ mb: 2 }}>
                      {teacher.teacher_profile.subjects &&
                        teacher.teacher_profile.subjects.length > 0 && (
                          <Box sx={{ mb: 2 }}>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ mb: 1, display: 'block' }}
                            >
                              Subjects
                            </Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                              {teacher.teacher_profile.subjects.slice(0, 3).map((subject) => (
                                <Chip key={subject} label={subject} size="small" />
                              ))}
                              {teacher.teacher_profile.subjects.length > 3 && (
                                <Chip
                                  label={`+${teacher.teacher_profile.subjects.length - 3}`}
                                  size="small"
                                />
                              )}
                            </Box>
                          </Box>
                        )}
                      {teacher.teacher_profile.office_location && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                          <RoomIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                          <Typography variant="caption" color="text.secondary">
                            {teacher.teacher_profile.office_location}
                          </Typography>
                        </Box>
                      )}
                      {teacher.teacher_profile.office_hours && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                          <ScheduleIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                          <Typography variant="caption" color="text.secondary">
                            {teacher.teacher_profile.office_hours}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  )}

                  {/* Module Count */}
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      mb: 2,
                      p: 2,
                      bgcolor: 'primary.50',
                      borderRadius: 1,
                    }}
                  >
                    <ModuleIcon sx={{ color: 'primary.main' }} />
                    <Typography variant="body2" color="text.secondary">
                      View assigned modules
                    </Typography>
                  </Box>

                  {/* Action Buttons */}
                  <Box sx={{ display: 'flex', gap: 1, mt: 'auto' }}>
                    <Button variant="outlined" fullWidth onClick={() => handleViewProfile(teacher)}>
                      Profile
                    </Button>
                    <Button
                      variant="contained"
                      fullWidth
                      endIcon={<ArrowIcon />}
                      onClick={() => handleViewModules(teacher.user_id)}
                    >
                      Modules
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Teacher Profile Dialog */}
      <Dialog open={profileDialogOpen} onClose={handleCloseProfile} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {selectedTeacher?.avatar ? (
              <AvatarDisplay
                avatar={selectedTeacher.avatar}
                name={selectedTeacher.name}
                role="teacher"
                size="large"
                showTooltip={false}
              />
            ) : (
              <Avatar
                sx={{
                  width: 56,
                  height: 56,
                  bgcolor: 'primary.main',
                  fontSize: '1.25rem',
                  fontWeight: 600,
                }}
              >
                {selectedTeacher && getInitials(selectedTeacher.name)}
              </Avatar>
            )}
            <Box>
              <Typography variant="h6">{selectedTeacher?.name}</Typography>
              <Typography variant="body2" color="text.secondary">
                Teacher Profile
              </Typography>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {selectedTeacher?.teacher_profile ? (
            <List>
              {/* Email */}
              <ListItem>
                <ListItemIcon>
                  <EmailIcon />
                </ListItemIcon>
                <ListItemText primary="Email" secondary={selectedTeacher.email} />
              </ListItem>

              {/* Phone */}
              {selectedTeacher.teacher_profile.phone && (
                <ListItem>
                  <ListItemIcon>
                    <PhoneIcon />
                  </ListItemIcon>
                  <ListItemText primary="Phone" secondary={selectedTeacher.teacher_profile.phone} />
                </ListItem>
              )}

              {/* Office Location */}
              {selectedTeacher.teacher_profile.office_location && (
                <ListItem>
                  <ListItemIcon>
                    <RoomIcon />
                  </ListItemIcon>
                  <ListItemText
                    primary="Office Location"
                    secondary={selectedTeacher.teacher_profile.office_location}
                  />
                </ListItem>
              )}

              {/* Office Hours */}
              {selectedTeacher.teacher_profile.office_hours && (
                <ListItem>
                  <ListItemIcon>
                    <ScheduleIcon />
                  </ListItemIcon>
                  <ListItemText
                    primary="Office Hours"
                    secondary={selectedTeacher.teacher_profile.office_hours}
                  />
                </ListItem>
              )}

              {/* Preferred Contact Method */}
              {selectedTeacher.teacher_profile.preferred_contact_method && (
                <ListItem>
                  <ListItemIcon>
                    <ContactIcon />
                  </ListItemIcon>
                  <ListItemText
                    primary="Preferred Contact"
                    secondary={selectedTeacher.teacher_profile.preferred_contact_method}
                  />
                </ListItem>
              )}

              {/* Subjects */}
              {selectedTeacher.teacher_profile.subjects &&
                selectedTeacher.teacher_profile.subjects.length > 0 && (
                  <>
                    <Divider sx={{ my: 2 }} />
                    <ListItem>
                      <ListItemIcon>
                        <SchoolIcon />
                      </ListItemIcon>
                      <ListItemText
                        primary="Subjects"
                        secondary={
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                            {selectedTeacher.teacher_profile.subjects.map((subject) => (
                              <Chip key={subject} label={subject} size="small" />
                            ))}
                          </Box>
                        }
                      />
                    </ListItem>
                  </>
                )}

              {/* Bio */}
              {selectedTeacher.teacher_profile.bio && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <ListItem>
                    <ListItemIcon>
                      <InfoIcon />
                    </ListItemIcon>
                    <ListItemText primary="About" secondary={selectedTeacher.teacher_profile.bio} />
                  </ListItem>
                </>
              )}
            </List>
          ) : (
            <Typography color="text.secondary" align="center" py={3}>
              No profile information available
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseProfile}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MyTeachers;
